import React, { useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { Form } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { formatDate, getUserDisplayName } from '../../common/aifeatures/utils'
import UserWithAvatar from '../../common/user/UserWithAvatar'
import { selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { SelectDropdown, SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import { Comment, CommentRelationType } from '../../../../../models/MeetingSupport'
import { useOutsideClick } from '../../../../../common/useOutsideClick'
import {
  clearNewComment,
  selectCommentedAiResultId,
  selectCommentsAreLoading,
  selectCommentUpdateIsLoading,
  selectFilteredComments,
  selectNewComment,
} from '../../../../../store/meetingSummary/meetingSupport'
import { useCreateComment, useUpdateComment } from '../hooks/api'
import './Comments.scss'
import { useZocksAuth } from '../../../../../components/ScopedBlock/hooks'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'

export const Comments: React.FC = () => {
  const dispatch = useAppDispatch()

  const comments = useAppSelector(selectFilteredComments)
  const newComment = useAppSelector(selectNewComment)
  const aiResultId = useAppSelector(selectCommentedAiResultId)

  useEffect(() => {
    return () => {
      dispatch(clearNewComment())
    }
  }, [dispatch])

  return (
    <div className='d-flex flex-column overflow-y-auto flex-grow-1 mb-4'>
      <div className='flex-grow-1 d-flex flex-column gap-3 zocks-py-1.25 zocks-px-1.25 comments-container'>
        {comments.length ? (
          comments
            .filter(({ relationType }) => relationType === CommentRelationType.AI_RESULT)
            .filter(({ relationId }) => (aiResultId ? relationId === aiResultId : true))
            .map((comment) => {
              const {
                id: commentId,
                metadata: { quote },
              } = comment
              const replies = comments.filter(({ relationId, relationType }) => relationId === commentId && relationType === CommentRelationType.COMMENT)
              return (
                <div key={commentId} className='d-flex flex-column gap-3'>
                  <div className='d-flex flex-column gap-2'>
                    <div className='d-flex flex-column p-3 quote-container'>{quote}</div>
                    <div className='d-flex flex-column p-3 gap-3'>
                      <CommentBody comment={comment} />
                      {replies.map((replyComment) => (
                        <CommentBody key={replyComment.id} comment={replyComment} />
                      ))}
                      <CommentReply commentId={comment.id} />
                    </div>
                  </div>
                </div>
              )
            })
        ) : (
          <>
            <div className='bold'>Be the first to add a comment</div>
            <div>Highlight any text in the presets to add a comment and start collaborating.</div>
          </>
        )}
        {newComment ? <NewCommentEditor /> : null}
      </div>
    </div>
  )
}

interface CommentBodyProps {
  comment: Comment
}
const CommentBody: React.FC<CommentBodyProps> = ({ comment }) => {
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const isCommentUpdateLoading = useAppSelector(selectCommentUpdateIsLoading)
  const updateComment = useUpdateComment()

  const {
    id: commentId,
    createdAt,
    updatedAt,
    user,
    comment: commentText,
    metadata: { deleted },
  } = comment
  const userName = getUserDisplayName(user)
  const edited = createdAt !== updatedAt

  const textRef = useRef<HTMLTextAreaElement>(null)

  const [editing, setEditing] = useState<boolean>(false)
  const [editedCommentValue, setEditedCommentValue] = useState<string>(commentText)

  useEffect(() => {
    if (textRef.current) {
      textRef.current.focus()
    }
  }, [textRef])

  const byCurrentUser = currentUserId === user?.id
  const handleStartEditing = () => {
    setEditing(true)
  }

  const handleStopEditing = () => {
    setEditing(false)
  }

  const handleEditedCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedCommentValue(e.target.value)
  }

  const handleUpdateComment = async () => {
    await updateComment(commentId, { comment: editedCommentValue })
    setEditing(false)
  }

  return (
    <div className='d-flex flex-column gap-2'>
      <div className='d-flex justify-content-between align-items-center position-relative'>
        <div className='d-flex align-items-center gap-2'>
          <div className='d-flex align-items-center gap-2 comment-user'>
            <UserWithAvatar name={userName} displayName={false} />
            <div className='paragraph-medium bold'>{userName}</div>
          </div>
          <div className='paragraph-medium zocks-text-gray-500'>{formatDate(updatedAt)}</div>
        </div>
        {byCurrentUser && !deleted ? (
          <div className='d-flex gap-2 align-items-center justify-content-center'>
            <CommentOptionsButton comment={comment} onStartEditing={handleStartEditing} />
          </div>
        ) : null}
      </div>
      {editing ? (
        <div className='d-flex flex-column p-2 justify-content-center gap-2 flex-grow-1 comment-text-area-container'>
          <Form.Control
            ref={textRef}
            className='border-0'
            autoFocus
            as='textarea'
            value={editedCommentValue}
            onChange={handleEditedCommentChange}
            placeholder='Add a comment…'
          />
          <div className='d-flex gap-1 justify-content-end align-items-center'>
            {!isCommentUpdateLoading ? (
              <Button variant='secondary' className='button-small p-2 border-0 bold' onClick={handleStopEditing} disabled={!editedCommentValue.trim()}>
                Cancel
              </Button>
            ) : null}
            <Button
              variant='primary'
              className='button-small p-2 border-0'
              onClick={handleUpdateComment}
              loading={isCommentUpdateLoading}
              disabled={!editedCommentValue.trim()}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className={classNames('paragraph-medium', { 'fst-italic zocks-text-gray-500': deleted })}>
            {deleted ? 'This comment has been deleted.' : commentText}
          </div>
          {edited && !deleted ? <div className='paragraph-medium zocks-text-gray-500'>Edited</div> : null}
        </>
      )}
    </div>
  )
}

interface CommentReplyProps {
  commentId: string
}

const CommentReply: React.FC<CommentReplyProps> = ({ commentId }) => {
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const isCommentUpdateLoading = useAppSelector(selectCommentUpdateIsLoading)

  const createComment = useCreateComment()

  const replyRef = useRef<HTMLDivElement>(null)

  const [replyValue, setReplyValue] = useState<string>('')
  const [rows, setRows] = useState<number>(1)
  const [active, setActive] = useState<boolean>(false)

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReplyValue(e.target.value)
  }

  const handleFocus = () => {
    setActive(true)
    setRows(3)
  }

  useOutsideClick(replyRef, () => {
    setActive(false)
    setRows(1)
  })

  const handleSaveReply = async () => {
    await createComment({ comment: replyValue, relationId: commentId, relationType: CommentRelationType.COMMENT })
    setReplyValue('')
  }

  return (
    <div className='d-flex gap-3 comment-user' ref={replyRef}>
      <div className={classNames('d-flex', { 'align-items-center': !active, 'align-items-start pt-2': active })}>
        <UserWithAvatar name={getUserDisplayName(currentUser)} displayName={false} />
      </div>
      <div
        className={classNames('d-flex flex-column justify-content-center gap-2 flex-grow-1 reply-text-area-container', {
          'reply-text-area-container-inactive': !active,
          'reply-text-area-container-active': active,
        })}
      >
        <Form.Control
          className='border-0'
          as='textarea'
          value={replyValue}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder='Add a reply…'
          rows={rows}
        />
        {active ? (
          <div className='d-flex gap-1 justify-content-end align-items-center'>
            <ScopedBlock
              scope={{
                scope: 'comment:create',
                superScope: ['comment:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <Button
                variant='primaryGhost'
                className='button-medium p-2 border-0'
                onClick={handleSaveReply}
                loading={isCommentUpdateLoading}
                disabled={!replyValue.trim()}
              >
                <i className='bi bi-send' />
              </Button>
            </ScopedBlock>
          </div>
        ) : null}
      </div>
    </div>
  )
}

interface CommentOptionsButtonProps {
  comment: Comment
  onStartEditing: () => void
}

const CommentOptionsButton: React.FC<CommentOptionsButtonProps> = ({ comment, onStartEditing }) => {
  const updateComment = useUpdateComment()

  const { hasScope } = useZocksAuth()
  const hasEditScope = hasScope('**', 'comment:update', ['comment:admin'])
  const hasDeleteScope = hasScope('**', 'comment:delete', ['comment:admin'])

  const handleDeleteComment = async () => {
    const { id: commentId, metadata } = comment
    await updateComment(commentId, { comment: '', relationMetadata: JSON.stringify({ ...metadata, deleted: true }) })
  }

  const options = useMemo(() => {
    return [
      { id: 'edit', label: 'Edit', disabled: !hasEditScope },
      { id: 'delete', label: 'Delete', disabled: !hasDeleteScope },
    ]
  }, [hasDeleteScope, hasEditScope])

  const handleOptionSelected = async (selectOption: SelectOption) => {
    const { id } = selectOption
    switch (id) {
      case 'edit':
        onStartEditing()
        return
      case 'delete':
        await handleDeleteComment()
        return
    }
  }

  return (
    <div className='comment-options-container d-flex align-items-center justify-content-center'>
      <div className='position-absolute'>
        <SelectDropdown
          className='p-1 justify-content-center'
          options={options}
          onOptionSelected={handleOptionSelected}
          icon='bi-three-dots-vertical'
          iconOnly
          hideArrows
        />
      </div>
    </div>
  )
}

const NewCommentEditor: React.FC = () => {
  const dispatch = useAppDispatch()

  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const newComment = useAppSelector(selectNewComment)
  const isCommentUpdateLoading = useAppSelector(selectCommentsAreLoading)

  const createComment = useCreateComment()

  const textRef = useRef<HTMLTextAreaElement>(null)

  const [newCommentValue, setNewCommentValue] = useState<string>('')

  useEffect(() => {
    if (textRef.current) {
      textRef.current.focus()
    }
  }, [textRef])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewCommentValue(e.target.value)
  }

  const handleSaveNewComment = async () => {
    if (!newComment) {
      return
    }
    const { aiResultId, markId, quote, basePath, path } = newComment
    const relationMetadata = JSON.stringify({ markId, quote, basePath, path })
    await createComment({ comment: newCommentValue, relationId: aiResultId, relationType: CommentRelationType.AI_RESULT, relationMetadata })
    dispatch(clearNewComment())
    setNewCommentValue('')
  }

  if (!newComment) {
    return null
  }

  return (
    <div className='d-flex flex-column gap-3'>
      <div className='d-flex flex-column p-3 quote-container'>{newComment.quote}</div>
      <div className='d-flex gap-3 new-comment-container'>
        <div>
          <UserWithAvatar name={getUserDisplayName(currentUser)} displayName={false} />
        </div>
        <div className='d-flex flex-column p-2 justify-content-center gap-2 flex-grow-1 comment-text-area-container'>
          <Form.Control
            ref={textRef}
            className='border-0'
            autoFocus
            as='textarea'
            value={newCommentValue}
            onChange={handleChange}
            placeholder='Add a comment…'
          />
          <div className='d-flex gap-1 justify-content-end align-items-center'>
            <ScopedBlock
              scope={{
                scope: 'comment:create',
                superScope: ['comment:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <Button
                variant='primaryGhost'
                className='button-medium p-2 border-0'
                onClick={handleSaveNewComment}
                loading={isCommentUpdateLoading}
                disabled={!newCommentValue.trim()}
              >
                <i className='bi bi-send' />
              </Button>
            </ScopedBlock>
          </div>
        </div>
      </div>
    </div>
  )
}
